import { Graph } from '../graph/graph';

interface RedundancyNode {
  articulationPoint: boolean;
}

interface RedundancyEdge {
  bridge: boolean;
}

interface DfsRedundancyNode extends RedundancyNode {
  visited: boolean;
  discoveryTime: number;
  low: number;
  parent: string | null;
}

const dfs = (
  graph: Graph<DfsRedundancyNode, RedundancyEdge>,
  nodeId: string,
  time = 0,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
) => {
  const node = graph.node(nodeId);
  if (!node) {
    return;
  }

  node.data.visited = true;
  node.data.discoveryTime = time + 1;
  node.data.low = time + 1;
  let child = 0;

  for (const { source, target } of graph.edges(nodeId)) {
    const nodeIsSource = source === nodeId;
    const adjacentNode = graph.node(nodeIsSource && target ? target : source);
    if (!adjacentNode) {
      return;
    }

    if (!adjacentNode.data.visited) {
      child += 1;
      adjacentNode.data.parent = node.id;
      dfs(graph, adjacentNode.id, time + 1);
      node.data.low = Math.min(node.data.low, adjacentNode.data.low);
      if (node.data.parent === null && child > 1) {
        node.data.articulationPoint = true;
      } else if (
        node.data.parent !== null &&
        adjacentNode.data.low >= node.data.discoveryTime
      ) {
        node.data.articulationPoint = true;
      }
      if (adjacentNode.data.low > node.data.discoveryTime) {
        const edgesBetween = Array.from(
          graph.edgesBetween(nodeId, adjacentNode.id),
        );
        // if there is more than one edge between the nodes, they are redundant
        if (edgesBetween.length === 1 && 0 in edgesBetween) {
          edgesBetween[0].data.bridge = true;
        }
      }
    } else if (node.data.parent !== adjacentNode.id) {
      node.data.low = Math.min(node.data.low, adjacentNode.data.discoveryTime);
    }
  }
  if (graph.hasSingleNeighbor(node.id)) {
    node.data.articulationPoint = true;
  }
};

export class RedundancyGraph<
  TNode extends { id: string; stack?: boolean },
  TEdge extends { id: string; source: string; target: string | null },
> {
  private nodes: TNode[];
  private edges: TEdge[];
  private nodeIsArticulationPointMap: Record<string, boolean> = {};
  private edgeIsBridgeMap: Record<string, boolean> = {};

  constructor(nodes: TNode[], edges: TEdge[]) {
    this.nodes = nodes;
    this.edges = edges;

    const redundancyGraph = new Graph<DfsRedundancyNode, RedundancyEdge>();
    for (const node of nodes) {
      redundancyGraph.addNode(node.id, {
        visited: false,
        discoveryTime: Number.POSITIVE_INFINITY,
        low: Number.POSITIVE_INFINITY,
        articulationPoint: false,
        parent: null,
      });
    }

    for (const edge of edges) {
      try {
        redundancyGraph.addEdge(edge.id, edge.source, edge.target, {
          bridge: false,
        });
      } catch {
        // noop - Do not add edge without `source` or `target` node
      }
    }

    for (const node of redundancyGraph.nodes()) {
      if (!node.data.visited) {
        dfs(redundancyGraph, node.id);
      }
    }

    for (const node of nodes) {
      if (redundancyGraph.node(node.id)?.data.articulationPoint) {
        this.nodeIsArticulationPointMap[node.id] = true;
      }
    }

    for (const edge of edges) {
      if (redundancyGraph.edge(edge.id)?.data.bridge) {
        this.edgeIsBridgeMap[edge.id] = true;
      }
    }
  }

  isNodeRedundant(node: TNode): boolean {
    if (node.stack) {
      return true;
    }
    return !this.nodeIsArticulationPointMap[node.id];
  }

  isEdgeRedundant(edge: TEdge): boolean {
    return !this.edgeIsBridgeMap[edge.id];
  }

  *redundantNodes(): IterableIterator<TNode> {
    for (const node of this.nodes) {
      if (this.isNodeRedundant(node)) {
        yield node;
      }
    }
  }

  *nonRedundantNodes(): IterableIterator<TNode> {
    for (const node of this.nodes) {
      if (!this.isNodeRedundant(node)) {
        yield node;
      }
    }
  }

  *redundantEdges(): IterableIterator<TEdge> {
    for (const edge of this.edges) {
      if (this.isEdgeRedundant(edge)) {
        yield edge;
      }
    }
  }

  *nonRedundantEdges(): IterableIterator<TEdge> {
    for (const edge of this.edges) {
      if (!this.isEdgeRedundant(edge)) {
        yield edge;
      }
    }
  }
}
